/**
 * Gallery (design: dark "room", category pills, photo grid, +/- size, full-screen viewer) on Caryn's real gallery API.
 * Premium photos show blurred with their price and open today's GalleryPurchaseModal.
 * Viewing time is reported with today's tracker (feeds the admin's VIP "gallery minutes").
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import GalleryPurchaseModal from '@/features/gallery/components/GalleryPurchaseModal'
import { useGallerySessionTracker } from '@/hooks/useGallerySessionTracker'
import { gallery, type Album, type GalleryImage } from '~/api/endpoints'
import { Icon } from '~/app/ui'
import { usePin } from '~/app/pin'
import { Viewer, type ViewerItem } from '~/app/Viewer'

const PAGE = 60
const COLS = [1, 2, 3, 4, 5]

export function Gallery() {
  useGallerySessionTracker()
  const navigate = useNavigate()
  const { go } = usePin()
  const [albums, setAlbums] = useState<Album[]>([])
  const [album, setAlbum] = useState<string | null>(null) // slug, null = All
  // owner 11-04: today's "Liked" and "Unlocked" filters are back (the photos she hearted / bought)
  const [special, setSpecial] = useState<null | 'liked' | 'unlocked'>(null)
  const [images, setImages] = useState<GalleryImage[]>([])
  const [total, setTotal] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [liked, setLiked] = useState<Set<string>>(new Set())
  const [bought, setBought] = useState<Set<string>>(new Set())
  const [cols, setCols] = useState(() => (window.innerWidth >= 900 ? 4 : 2))
  const [open, setOpen] = useState<number | null>(null)
  const [buy, setBuy] = useState<GalleryImage | null>(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    gallery.albums().then(setAlbums)
    gallery.likedIds().then(setLiked)
    gallery.purchasedIds().then(setBought)
  }, [])
  useEffect(() => {
    let alive = true
    setLoading(true); setFailed(false)
    // Liked / Unlocked: the whole gallery list (full-quality links, same grid), filtered to her hearted / bought photos
    const loadAll = async () => {
      const all: GalleryImage[] = []
      let ok = true
      for (let page = 0; page < 30; page++) {
        const r = await gallery.images(PAGE, page * PAGE)
        ok = ok && r.ok
        all.push(...r.images.filter((x) => !all.some((p) => p.id === x.id)))
        if (!r.images.length || (r.total !== null && all.length >= r.total)) break
      }
      const keep = special === 'liked' ? liked : bought
      const imgs = all.filter((g) => keep.has(g.id))
      return { images: imgs, total: imgs.length, ok }
    }
    const load = album ? gallery.album(album).then((imgs) => ({ images: imgs, total: imgs.length, ok: true })) : special ? loadAll() : gallery.images(PAGE, 0)
    load.then((r) => { if (!alive) return; setImages(r.images); setTotal(r.total); setFailed(!r.ok); setLoading(false) })
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [album, special, reload])

  const more = async () => {
    const r = await gallery.images(PAGE, images.length)
    setImages((prev) => [...prev, ...r.images.filter((x) => !prev.some((p) => p.id === x.id))])
  }
  const locked = (g: GalleryImage) => !g.hasAccess && !bought.has(g.id)
  // SECURITY (owner 11-04): the full-screen viewer only ever holds photos she can see - locked / paid photos are NOT in it,
  // so swiping from a free photo can never reveal a paid one (it used to show the paid photo's picture in full).
  // Tapping a locked photo opens the unlock window instead.
  const viewable = useMemo(() => images.filter((g) => !locked(g)), [images, bought]) // eslint-disable-line react-hooks/exhaustive-deps
  const open1 = (g: GalleryImage) => {
    if (locked(g)) { if (g.access_level === 'premium') setBuy(g); return }
    const at = viewable.findIndex((x) => x.id === g.id)
    if (at >= 0) setOpen(at)
  }
  const viewerItems: ViewerItem[] = useMemo(() => viewable.map((g) => ({
    id: g.id, galleryId: g.id, liked: liked.has(g.id), alt: g.title || 'Photo',
    src: g.urls.full || g.urls.medium || g.urls.thumbnail,
  })), [viewable, liked])

  const like = async (it: ViewerItem) => {
    if (!it.galleryId) return
    const id = it.galleryId
    setLiked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n }) // optimistic
    const res = await gallery.like(id)
    if (res !== null) setLiked((s) => { const n = new Set(s); res ? n.add(id) : n.delete(id); return n })
  }

  return (
    <main className="room-view" id="main" aria-label="Gallery">
      <div className="room-top">
        <button className="room-back" type="button" aria-label="Back to home" onClick={() => navigate('/home')}><Icon id="i-back" /></button>
        <ul className="pills" aria-label="Gallery categories">
          <li><button className="pill-btn" type="button" aria-current={album === null && !special} onClick={() => { setSpecial(null); setAlbum(null) }}>All</button></li>
          {liked.size > 0 && <li><button className="pill-btn" type="button" aria-current={special === 'liked'} onClick={() => { setAlbum(null); setSpecial('liked') }}>Liked <span className="v2-pill-count">{liked.size}</span></button></li>}
          {bought.size > 0 && <li><button className="pill-btn" type="button" aria-current={special === 'unlocked'} onClick={() => { setAlbum(null); setSpecial('unlocked') }}>Unlocked <span className="v2-pill-count">{bought.size}</span></button></li>}
          {albums.map((a) => <li key={a.id}><button className="pill-btn" type="button" aria-current={album === a.slug && !special} onClick={() => { setSpecial(null); setAlbum(a.slug) }}>{a.name}</button></li>)}
        </ul>
      </div>

      {loading ? <p className="v2-room-msg" role="status">Loading photos…</p>
        : failed ? <p className="v2-room-msg">The gallery didn’t load. <button className="link-btn" type="button" onClick={() => setReload((n) => n + 1)}>Try again</button></p>
        : images.length === 0 ? <p className="v2-room-msg">No photos here yet.</p>
        : (
          <div className="masonry v2-rows" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
            {images.map((g, i) => (
              <button key={g.id} className={`m-item${locked(g) ? ' v2-premium' : ''}`} type="button" style={{ aspectRatio: `${g.original_width || 3} / ${g.original_height || 4}`, position: 'relative', background: g.dominant_color || undefined }}
                aria-label={locked(g) && g.price ? `Unlock photo for $${g.price}` : `Open photo ${i + 1}`} onClick={() => open1(g)}>
                {/* SECURITY: a locked photo uses the server's pre-blurred placeholder when it has one, so the clear thumbnail is never loaded */}
                {(locked(g) ? (g.urls.placeholder || g.urls.thumbnail) : g.urls.thumbnail) && <img src={(locked(g) ? (g.urls.placeholder || g.urls.thumbnail) : g.urls.thumbnail) || undefined} alt="" loading="lazy" decoding="async" width={640} height={960} />}
                {!locked(g) && <HiRes src={g.urls.full || g.urls.medium || null} thumb={g.urls.thumbnail} />}
                {locked(g) && <span className="v2-gate" aria-hidden="true"><Icon id="i-lock" /></span>}
                {/* owner 11-04: no corner label on paid photos - only the centre lock over the blur */}
              </button>
            ))}
          </div>
        )}
      {!album && !special && total !== null && images.length < total && !loading && (
        <p className="room-note"><button className="link-btn" type="button" onClick={more}>Show more photos</button></p>
      )}

      <div className="zoom-glass" role="group" aria-label="Photo size">
        <button type="button" aria-label="Larger photos" disabled={cols <= COLS[0]} onClick={() => setCols((c) => Math.max(COLS[0], c - 1))}><Icon id="i-plus" /></button>
        <button type="button" aria-label="Smaller photos" disabled={cols >= COLS[COLS.length - 1]} onClick={() => setCols((c) => Math.min(COLS[COLS.length - 1], c + 1))}><Icon id="i-minus" /></button>
      </div>

      {open !== null && (
        <Viewer items={viewerItems} start={open} onClose={() => setOpen(null)} onLike={like}
          onTextMe={(it) => go(`/text?replyToGalleryImage=${encodeURIComponent(it.galleryId || it.id)}`)} />
      )}
      {buy && (
        <div className="tw-scope">
          <GalleryPurchaseModal image={buy as any} onClose={() => setBuy(null)}
            onSuccess={() => { setBought((s) => new Set(s).add(buy.id)); setBuy(null); setReload((n) => n + 1) }} />
        </div>
      )}
    </main>
  )
}

/** owner 11-04 "highest quality": the sharp photo fades in over the quick thumbnail once it has loaded
 *  (always the FULL-quality photo the server gives this fan - same as the full-screen viewer - never the medium size) */
function HiRes({ src, thumb }: { src: string | null; thumb?: string | null }) {
  const [on, setOn] = useState(false)
  if (!src || src === thumb) return null
  return <img className={`v2-hires${on ? ' on' : ''}`} src={src} alt="" loading="lazy" decoding="async" onLoad={() => setOn(true)} />
}
